import React, { useState } from 'react';
import { BookPreviewHeader } from './BookPreviewHeader';
import { BookPreviewContent } from './BookPreviewContent';
import { BookPreviewNavigation } from './BookPreviewNavigation';
import './BookPreview.css';

export interface BookPreviewData {
  id: string;
  title: string;
  description: string;
  cover_image?: string;
  author?: string;
  category?: string;
  status: 'draft' | 'published' | 'archived';
  contents: BookPreviewChapter[];
  created_at?: string;
  updated_at?: string;
}

export interface BookPreviewChapter {
  id: string;
  title: string;
  order: number;
  children: BookPreviewSection[];
  duration?: number;
  start_day_offset?: number;
  start_day_offset_relative_to?: 'book' | 'chapter' | 'section';
  start_day_offset_reference_id?: string;
}

export interface BookPreviewSection {
  id: string;
  title: string;
  order: number;
  type: 'chapter' | 'container' | 'file' | 'content' | 'text' | 'image' | 'pdf' | 'video' | 'audio' | 'interactive' | 'quiz';
  duration?: number;
  start_day_offset?: number;
  start_day_offset_relative_to?: 'book' | 'chapter' | 'section';
  start_day_offset_reference_id?: string;
  file_id?: string;
  file?: {
    file_id: string;
    file_name: string;
    file_type: string;
    file_size: number;
    file_url: string;
  };
  content_block?: {
    content_type: 'html' | 'pdf' | 'scorm' | 'html_package' | 'pptx' | 'media' | 'quiz' | 'video' | 'audio' | 'image' | 'embed' | 'list' | 'text' | 'flashcard';
    payload: any;
  };
  content_blocks?: Array<{
    content_type: 'html' | 'pdf' | 'scorm' | 'html_package' | 'pptx' | 'media' | 'quiz' | 'video' | 'audio' | 'image' | 'embed' | 'list' | 'text' | 'flashcard';
    payload: any;
    appearance?: {
      background?: {
        color?: string;
        image?: string | null;
        fit?: 'cover' | 'contain' | 'fill';
        opacity?: number;
      };
      padding?: {
        top?: string;
        bottom?: string;
        left?: string;
        right?: string;
      };
    };
    layout?: {
      mode: 'flow' | 'fixed' | 'grid';
    };
    timing?: {
      startMs?: number;
      endMs?: number | null;
      autoplay?: boolean;
    };
  }>;
  children?: BookPreviewSection[];
}

interface BookPreviewProps {
  bookData: BookPreviewData;
  onClose?: () => void;
  showNavigation?: boolean;
  showHeader?: boolean;
  className?: string;
}

export const BookPreview: React.FC<BookPreviewProps> = ({
  bookData,
  onClose,
  showNavigation = true,
  showHeader = true,
  className = ''
}) => {
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const currentChapter = bookData.contents[currentChapterIndex];
  const currentSection = currentChapter?.children[currentSectionIndex];

  const totalChapters = bookData.contents.length;
  const totalSections = currentChapter?.children.length || 0;

  const handleNext = () => {
    if (currentSectionIndex < totalSections - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    } else if (currentChapterIndex < totalChapters - 1) {
      setCurrentChapterIndex(currentChapterIndex + 1);
      setCurrentSectionIndex(0);
    }
  };

  const handlePrevious = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    } else if (currentChapterIndex > 0) {
      setCurrentChapterIndex(currentChapterIndex - 1);
      setCurrentSectionIndex(bookData.contents[currentChapterIndex - 1].children.length - 1);
    }
  };

  const handleChapterSelect = (chapterIndex: number) => {
    setCurrentChapterIndex(chapterIndex);
    setCurrentSectionIndex(0);
  };

  const handleSectionSelect = (sectionIndex: number) => {
    setCurrentSectionIndex(sectionIndex);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const progress = totalChapters > 0 ? 
    ((currentChapterIndex + (currentSectionIndex + 1) / totalSections) / totalChapters) * 100 : 0;

  return (
    <div className={`book-preview ${isFullscreen ? 'fullscreen' : ''} ${className}`}>
      {showHeader && (
        <BookPreviewHeader
          bookData={bookData}
          currentChapter={currentChapter}
          currentSection={currentSection}
          progress={progress}
          onClose={onClose}
          onToggleFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
        />
      )}

      <div className="book-preview-body">
        {showNavigation && (
          <BookPreviewNavigation
            chapters={bookData.contents}
            currentChapterIndex={currentChapterIndex}
            currentSectionIndex={currentSectionIndex}
            onChapterSelect={handleChapterSelect}
            onSectionSelect={handleSectionSelect}
            isCollapsed={isFullscreen}
          />
        )}

        <BookPreviewContent
          currentSection={currentSection}
          onNext={handleNext}
          onPrevious={handlePrevious}
          canGoNext={currentChapterIndex < totalChapters - 1 || currentSectionIndex < totalSections - 1}
          canGoPrevious={currentChapterIndex > 0 || currentSectionIndex > 0}
          isFullscreen={isFullscreen}
        />
      </div>
    </div>
  );
};

export default BookPreview;

